import axios from "axios"
import { useAuthToken } from "@/composables/useAuthToken"

/**
 * Navigation item structure from backend
 */
export interface NavigationItem {
  key: string
  label: string
  icon?: string
  url?: string
  target?: string
  children?: NavigationItem[]
}

/**
 * Response structure for navigation API
 */
export interface NavigationResponse {
  success: boolean
  data: NavigationItem[]
  error?: string
}

/**
 * Separate axios instance for navigation that does NOT have global interceptors.
 * This prevents auth error redirects when navigation loading fails.
 */
const navigationAxios = axios.create({
  timeout: 10000
})

/**
 * Service for fetching navigation data from the backend.
 * The navigation structure is managed centrally on the backend (NavigationService.php)
 * and respects user permissions.
 *
 * IMPORTANT: This service uses a SEPARATE axios instance to avoid
 * triggering automatic redirects on auth errors. Navigation loading should be
 * non-critical - if it fails, the app should still work without navigation.
 */
export default class NavigationService {
  /**
   * Fetch the navigation structure for the current user.
   * Returns navigation items filtered by user permissions.
   *
   * This method handles errors gracefully without triggering auth redirects.
   *
   * @returns Promise<NavigationResponse> Navigation items
   */
  public async getNavigation(): Promise<NavigationResponse> {
    try {
      const authToken = useAuthToken()
      const token = authToken.getToken()

      // @ts-expect-error - Vite injects import.meta.env at build time
      const apiUrl = import.meta.env.VITE_API_URL + "/navigation/get"

      const headers: Record<string, string> = {
        "Content-Type": "application/json"
      }

      if (token) {
        headers["Authorization"] = `Bearer ${token}`
      }

      // Use separate axios instance without global interceptors
      const response = await navigationAxios.get<NavigationItem[]>(apiUrl, {
        headers
      })

      return {
        success: true,
        data: response.data || []
      }
    } catch (error) {
      // Log error but don't trigger auth redirect
      console.warn("Navigation loading failed (non-critical):", error)

      // Return empty navigation on error - app continues to work
      return {
        success: false,
        data: [],
        error: "Navigation konnte nicht geladen werden"
      }
    }
  }
}
